<script lang="ts">
  import { api } from '$shared/api';
  import Avatar from '$shared/Avatar.svelte';
  import type { SeasonBannerData } from '$types/index';
  import { weekEndLabel } from '$lib/utils/weekLabel';

  let data = $state<SeasonBannerData | null>(null);

  $effect(() => {
    api
      .get<SeasonBannerData>('/season-banner')
      .then((d) => {
        data = d;
      })
      .catch(() => {});
  });

  const progressPct = $derived(data ? Math.max(0, Math.min(100, ((7 - data.days_remaining) / 7) * 100)) : 0);

  // 2A "explicit end time" — a static label computed FROM end_date, never a
  // hardcoded clock time (the transitional week ends off the usual 19:00 PT
  // mark). getSeasonBanner() always returns the active week, so "ended" only
  // guards a stale/late-arriving response.
  const weekEndedLine = $derived(
    data ? (new Date(data.end_date).getTime() <= Date.now() ? 'Week ended' : weekEndLabel(data.end_date)) : '',
  );
</script>

{#if data}
  <div class="season-banner">
    {#if data.previous_winner}
      <div class="winner-announce">
        <Avatar username={data.previous_winner.username} avatarUrl={data.previous_winner.avatar_url} size="sm" />
        <div class="winner-info">
          <span class="winner-label">Week {data.previous_winner.season_number} Champion</span>
          <a href="#/users/{data.previous_winner.username}" class="winner-name">@{data.previous_winner.username}</a>
          <span class="winner-pts">{data.previous_winner.points} pts</span>
        </div>
      </div>
    {/if}

    <div class="season-current">
      <div class="season-header">
        <span class="season-title">WEEK {data.season_number}</span>
        <span class="season-countdown">{weekEndedLine}</span>
      </div>
      <div class="progress-bar">
        <div class="progress-fill" style="width: {progressPct}%"></div>
      </div>
      <a href="#/leaderboard" class="season-link">View Leaderboard</a>
    </div>
  </div>
{/if}

<style>
  .season-banner {
    background: linear-gradient(135deg, var(--surface) 0%, var(--surface-2) 100%);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 1rem 1.25rem;
    margin-bottom: 1.5rem;
  }

  .winner-announce {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding-bottom: 0.75rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid var(--border);
  }
  .winner-info {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }
  .winner-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--gold);
  }
  .winner-name {
    font-weight: 600;
    color: var(--text);
    text-decoration: none;
  }
  .winner-name:hover {
    color: var(--cyan);
  }
  .winner-pts {
    font-size: 0.8rem;
    color: var(--text-muted);
  }

  .season-current {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .season-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .season-title {
    font-weight: 700;
    font-size: 1rem;
    color: var(--text);
  }
  .season-countdown {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--cyan);
  }

  .progress-bar {
    height: 4px;
    background: var(--surface-2);
    border-radius: 2px;
    overflow: hidden;
  }
  .progress-fill {
    height: 100%;
    background: var(--cyan);
    border-radius: 2px;
    transition: width 0.3s ease;
  }

  .season-link {
    font-size: 0.8rem;
    color: var(--text-secondary);
    text-decoration: none;
    text-align: right;
  }
  .season-link:hover {
    color: var(--cyan);
  }
</style>
